import type {
  BaseAnalyticsFilter,
  SessionDetailFilter,
  WellnessFilter,
} from '@repo/shared/dtos/analytics/AnalyticsFilterDto';

export const analyticsQueryKeys = {
  all: ['analytics'] as const,

  sessions: {
    all: () => [...analyticsQueryKeys.all, 'sessions'] as const,
    list: (filters: BaseAnalyticsFilter) =>
      [...analyticsQueryKeys.sessions.all(), 'list', filters] as const,
    minutes: (filters: BaseAnalyticsFilter) =>
      [...analyticsQueryKeys.sessions.all(), 'minutes', filters] as const,
    trends: (filters: BaseAnalyticsFilter) =>
      [...analyticsQueryKeys.sessions.all(), 'trends', filters] as const,
    sensorUtilization: (filters: BaseAnalyticsFilter) =>
      [...analyticsQueryKeys.sessions.all(), 'sensor-utilization', filters] as const,
    byExperience: (filters: BaseAnalyticsFilter) =>
      [...analyticsQueryKeys.sessions.all(), 'by-experience', filters] as const,
    dashboard: (filters: BaseAnalyticsFilter) =>
      [...analyticsQueryKeys.sessions.all(), 'dashboard', filters] as const,
    topExperiences: (filters: BaseAnalyticsFilter) =>
      [...analyticsQueryKeys.sessions.all(), 'top-experiences', filters] as const,
    monthlyDetails: (year: number, month: number, filters: SessionDetailFilter) =>
      [...analyticsQueryKeys.sessions.all(), 'details', 'monthly', year, month, filters] as const,
    experienceDetails: (experienceTitle: string, filters: SessionDetailFilter) =>
      [
        ...analyticsQueryKeys.sessions.all(),
        'details',
        'experience',
        experienceTitle,
        filters,
      ] as const,
  },

  utilization: {
    all: () => [...analyticsQueryKeys.all, 'utilization'] as const,
    dashboard: (filters: BaseAnalyticsFilter) =>
      [...analyticsQueryKeys.utilization.all(), 'dashboard', filters] as const,
    kpis: (filters: BaseAnalyticsFilter) =>
      [...analyticsQueryKeys.utilization.all(), 'kpis', filters] as const,
    monthlyTrends: (filters: BaseAnalyticsFilter & { monthsBack?: string }) =>
      [...analyticsQueryKeys.utilization.all(), 'monthly-trends', filters] as const,
    userRankings: (filters: BaseAnalyticsFilter & { orderBy?: string; limit?: string }) =>
      [...analyticsQueryKeys.utilization.all(), 'user-rankings', filters] as const,
    topUsersThisMonth: (filters: BaseAnalyticsFilter & { limit?: string }) =>
      [...analyticsQueryKeys.utilization.all(), 'top-users-this-month', filters] as const,
  },

  users: {
    all: () => [...analyticsQueryKeys.all, 'users'] as const,
    search: (searchTerm: string, filters: BaseAnalyticsFilter, limit: number) =>
      [...analyticsQueryKeys.users.all(), 'search', searchTerm, filters, limit] as const,
    utilization: (firstName: string, lastName: string, filters: BaseAnalyticsFilter) =>
      [...analyticsQueryKeys.users.all(), 'utilization', firstName, lastName, filters] as const,
    monthlyExperiences: (
      firstName: string,
      lastName: string,
      monthYear: string,
      filters: BaseAnalyticsFilter,
    ) =>
      [
        ...analyticsQueryKeys.users.all(),
        'monthly-experiences',
        firstName,
        lastName,
        monthYear,
        filters,
      ] as const,
    batchUtilization: (userParams: string, filters: BaseAnalyticsFilter) =>
      [...analyticsQueryKeys.users.all(), 'batch-utilization', userParams, filters] as const,
  },

  wellness: {
    all: () => [...analyticsQueryKeys.all, 'wellness'] as const,
    metrics: (filters: WellnessFilter) =>
      [...analyticsQueryKeys.wellness.all(), 'metrics', filters] as const,
    completionRate: (filters: WellnessFilter) =>
      [...analyticsQueryKeys.wellness.all(), 'completion-rate', filters] as const,
  },

  sessionSummary: {
    all: () => [...analyticsQueryKeys.all, 'session-summary'] as const,
    current: () => [...analyticsQueryKeys.sessionSummary.all(), 'current'] as const,
  },

  export: {
    all: () => [...analyticsQueryKeys.all, 'export'] as const,
    units: () => [...analyticsQueryKeys.export.all(), 'units'] as const,
    report: (endpoint: string, params: string) =>
      [...analyticsQueryKeys.export.all(), 'report', endpoint, params] as const,
  },
} as const;
